"use client";

import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useIntersectionObserver } from "@faire/web--source/ui/hooks/useIntersectionObserver";
import { useMergedRefs } from "@faire/web--source/ui/hooks/useMergedRefs";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import React, { forwardRef, useContext } from "react";

import { useSDUIActionsHandler } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/useSDUIActionsHandler";
import { logLegacyViewEvent as logLegacyViewEventHandler } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logLegacyViewEvent";
import { logSDUIEvent } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import { LoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from "../../SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";

export type SDUIOnViewHandlerProps = {
  className?: string;
  componentStyle?: React.CSSProperties;
  children: React.ReactNode;
  loggingData: LoggingEventParams;
  onViewActions: IAction[];
  logSDUIViewEvent?: boolean;
  // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
  // logLegacyViewEvent should be removed once banners and grids are fully migrated to SDUI
  // and data pipelines and dashboards no longer need the layout element events
  logLegacyViewEvent?: boolean;
  // This is only true when grid optimizations are enabled and will be passed in from the grid renderer
  shouldSetIsScrollingTopToFalseOnView?: boolean;
} & Partial<Pick<HTMLDivElement, "ariaHidden">>;

export const SDUIOnViewHandler = forwardRef<
  HTMLDivElement,
  SDUIOnViewHandlerProps
>(
  (
    {
      className,
      componentStyle,
      children,
      loggingData,
      onViewActions,
      logSDUIViewEvent,
      logLegacyViewEvent,
      shouldSetIsScrollingTopToFalseOnView,
      ariaHidden,
    },
    ref
  ) => {
    const { setIsScrollingToTop } = useContext(
      FilterablePaginatedSectionContext
    );

    const actionsHandler = useSDUIActionsHandler(onViewActions, loggingData);

    const viewRef = useIntersectionObserver(
      (entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        actionsHandler();
        if (logSDUIViewEvent && loggingData.view_display_page) {
          logSDUIEvent("VIEW", loggingData);
        }
        if (shouldSetIsScrollingTopToFalseOnView) {
          setIsScrollingToTop?.(false);
        }
      },
      {
        threshold: 0,
        rootMargin: "0px",
      }
    );

    const legacyViewEventRef = useDoActionWhenVisible(() => {
      if (
        logLegacyViewEvent &&
        "component_name" in loggingData &&
        loggingData.component_name &&
        loggingData.view_display_page
      ) {
        const componentName = loggingData.component_name;
        const requestId = loggingData.request_id ?? "";
        const actionContent =
          "action_content" in loggingData
            ? (loggingData.action_content ?? "")
            : "";
        logLegacyViewEventHandler(loggingData.view_display_page, {
          componentName,
          requestId,
          actionContent,
        });
      }
    });

    const refsToMerge = [ref];
    if (
      (logSDUIViewEvent ||
        onViewActions.length > 0 ||
        shouldSetIsScrollingTopToFalseOnView
      )
    ) {
      refsToMerge.push(viewRef);
    }

    const validLegacyViewEventConditions =
      logLegacyViewEvent &&
      "component_name" in loggingData &&
      loggingData.component_name &&
      loggingData.view_display_page;

    if (validLegacyViewEventConditions) {
      refsToMerge.push(legacyViewEventRef);
    }

    const mergedRefs = useMergedRefs(...refsToMerge);
    return (
      <div
        className={className}
        style={componentStyle}
        ref={mergedRefs}
        aria-hidden={ariaHidden === "true"}
      >
        {children}
      </div>
    );
  }
);
